<template>
    <div>
        <!--Edit Academic Dialog-->
        <Dialog :visible.sync="academicDialog" :style="{width: '450px'}" header="Academic Qualifications Details"
                :modal="true" :maximizable="true" class="p-fluid">

            <div class="p-field" v-if="createAcademic">
                <label for="staff">Staff</label>
                <Dropdown id="staff" v-model="academic.staff"
                          :options="staffs" optionLabel="name" :filter="true"
                          :class="{'p-invalid': submitted && $v.academic.staff.$invalid}"/>
                <small class="p-invalid" v-if="submitted && $v.academic.staff.$invalid">Staff is
                    required.</small>
            </div>

            <div class="p-field">
                <label for="level">Level</label>
                <Dropdown id="level" v-model="academic.level" :options="levels" optionLabel="name"
                          optionValue="name" :filter="true"
                          :class="{'p-invalid': submitted && $v.academic.level.$invalid}"/>
                <small class="p-invalid" v-if="submitted && $v.academic.level.$invalid">Level is
                    required.</small>
            </div>

            <div class="p-field">
                <label for="discipline">Discipline</label>
                <Dropdown id="discipline" v-model="academic.discipline" :options="disciplines" optionLabel="name"
                          optionValue="name" :filter="true"
                          :class="{'p-invalid': submitted && $v.academic.discipline.$invalid}"/>
                <small class="p-invalid" v-if="submitted && $v.academic.discipline.$invalid">Discipline is
                    required.</small>
            </div>

            <div class="p-field">
                <label for="title">Title</label>
                <InputText id="title" v-model.trim="academic.title" required="true" autofocus
                           :class="{'p-invalid': submitted && $v.academic.title.$invalid}"/>
                <small class="p-invalid" v-if="submitted && $v.academic.title.$invalid">Title is
                    required.</small>
            </div>

            <div class="p-field">
                <label for="description">Description</label>
                <InputText id="description" v-model.trim="academic.description" required="true"/>
            </div>

            <div class="p-field">
                <label for="institution">Institution</label>
                <InputText id="institution" v-model.trim="academic.institution" required="true"
                           :class="{'p-invalid': submitted && $v.academic.institution.$invalid}"/>
                <small class="p-invalid" v-if="submitted && $v.academic.institution.$invalid">Institution is
                    required.</small>
            </div>

            <div class="p-field">
                <label for="date">Awarded Month (MM/YYYY)</label>
                <Calendar id="date" v-model="academic.date" view="month" dateFormat="mm/yy"
                          :yearNavigator="true" :yearRange="`1950:${new Date().getFullYear()}`" placeholder="MM/YYYY"
                          :class="{'p-invalid': submitted && $v.academic.date.$invalid}"/>
                <small class="p-invalid" style="margin-right: 1em" v-if="submitted && !$v.academic.date.required">
                    Date is required.
                </small>
                <small class="p-invalid" v-if="submitted && !$v.academic.date.dateFormat">
                    Date is in wrong format.
                </small>
            </div>

            <template #footer>
                <Button label="Cancel" icon="pi pi-times" class="p-button-text" @click="hideDialog"/>
                <Button label="Save" icon="pi pi-check" class="p-button-text" @click="saveAcademic"/>
            </template>
        </Dialog>


        <!--Delete Academic Dialog-->
        <Dialog :visible.sync="deleteAcademicDialog" :style="{width: '450px'}" header="Confirm" :modal="true">
            <div class="confirmation-content">
                <i class="pi pi-exclamation-triangle p-mr-3" style="font-size: 2rem"/>
                <span v-if="academic">Are you sure you want to delete <b>{{ academic.title }}</b>?</span>
            </div>
            <template #footer>
                <Button label="No" icon="pi pi-times" class="p-button-text"
                        @click="deleteAcademicDialog = false"/>
                <Button label="Yes" icon="pi pi-check" class="p-button-text" @click="deleteAcademic"/>
            </template>
        </Dialog>

        <!--Delete Academics Dialog-->
        <Dialog :visible.sync="deleteAcademicsDialog" :style="{width: '450px'}" header="Confirm" :modal="true">
            <div class="confirmation-content">
                <i class="pi pi-exclamation-triangle p-mr-3" style="font-size: 2rem"/>
                <span v-if="academic">Are you sure you want to delete the selected Academic Qualifications?</span>
            </div>
            <template #footer>
                <Button label="No" icon="pi pi-times" class="p-button-text"
                        @click="deleteAcademicsDialog = false"/>
                <Button label="Yes" icon="pi pi-check" class="p-button-text"
                        @click="deleteSelectedAcademics"/>
            </template>
        </Dialog>

        <!--Verify Academics Dialog-->
        <Dialog :visible.sync="toggleAcademicsDialog" :style="{width: '450px'}" header="Confirm" :modal="true">
            <div class="confirmation-content">
                <i class="pi pi-exclamation-triangle p-mr-3" style="font-size: 2rem"/>
                <span v-if="academic">Are you sure you want to {{ verifyAcademics ? 'verify' : 'unverify' }} the selected academic?</span>
            </div>
            <template #footer>
                <Button label="No" icon="pi pi-times" class="p-button-text"
                        @click="toggleAcademicsDialog = false"/>
                <Button label="Yes" icon="pi pi-check" class="p-button-text"
                        @click="toggleSelectedAcademics"/>
            </template>
        </Dialog>
    </div>
</template>

<script>
import {Component, Prop, Vue, Watch} from "vue-property-decorator";

Component.registerHooks(['validations']);
import {required} from 'vuelidate/lib/validators'
import Toast from 'primevue/toast';
import Dialog from "primevue/dialog";
import Dropdown from 'primevue/dropdown';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import InputSwitch from 'primevue/inputswitch';
import Calendar from 'primevue/calendar';
import AcademicService from "@/service/AcademicService";
import StaffService from "@/service/StaffService";
import store from "../../store";

/**
 * Class-component
 */
@Component({
    components: {
        Toast: Vue.extend(Toast),
        Dialog: Vue.extend(Dialog),
        Dropdown: Vue.extend(Dropdown),
        InputText: Vue.extend(InputText),
        Button: Vue.extend(Button),
        InputSwitch: Vue.extend(InputSwitch),
        Calendar: Vue.extend(Calendar)
    }
})
export default class AcademicDialog extends Vue {
    createAcademic = false;
    staffService = new StaffService();
    staffs = [];
    academicService = new AcademicService();
    levels = this.academicService.availableLevels();
    disciplines = this.academicService.availableDisciplines();
    academicDialog = false;
    deleteAcademicDialog = false;
    deleteAcademicsDialog = false;
    toggleAcademicsDialog = false;
    verifyAcademics = false;
    academic = {};
    submitted = false;
    selectedAcademics = [];
    toastSuccess = {
        severity: 'success',
        summary: 'Successful',
        detail: '',
        life: 3000
    };
    toastError = {
        severity: 'error',
        summary: 'Error',
        detail: '',
        life: 3000
    };

    validations() {
        const dateFormat = value => {
            if (typeof value === 'string' && !value.match(/^\d{1,2}\/\d{4}$/g)) return false
            return this.$moment(value, "MM/YYYY").isSameOrBefore(this.$moment())
        }
        return {
            academic: {
                staff: {required},
                level: {required},
                discipline: {required},
                title: {required},
                institution: {required},
                date: {required, dateFormat}
            }
        }
    }

    @Watch('createAcademic')
    async onCreateAcademicChanged() {
        if (this.createAcademic) this.staffs = await this.staffService.getStaffs();
    }

    @Watch('academic')
    async onAcademicChanged() {
        if (this.academic.date) this.academic.date = this.$formatDate(this.academic.date)
    }

    hideDialog() {
        this.academicDialog = false;
        this.submitted = false;
    }

    async saveAcademic() {
        this.submitted = true;

        if (this.$v.$invalid) {
            return; // invalid input
        }

        const academicObj = {
            'verified': 0,
            _jv: {
                type: 'academics',
                relationships: {
                    staff: {data: {id: this.academic.staff._jv.id, type: 'staffs'}}
                }
            }
        };

        ['level', 'discipline', 'title', 'description', 'institution'].forEach(el => {
            academicObj[el] = this.academic[el]
        });

        academicObj['date'] = this.$moment(this.academic.date, "MM/YYYY").toISOString()

        try {
            if (this.academic._jv) {
                // PATCH
                academicObj._jv.id = this.academic._jv.id
                await this.academicService.updateAcademic(academicObj)
            } else {
                // POST
                await this.academicService.createAcademic(academicObj);
            }
            this.$emit('updated')
            this.toastSuccess.detail = 'Academic Qualifications Updated'
            this.$toast.add(this.toastSuccess);
        } catch (e) {
            this.toastError.detail = e.message
            this.$toast.add(this.toastError);
            this.academicService.logError(e).forEach(err => {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Details',
                    detail: err
                });
            })
        }

        this.academicDialog = false;
        this.academic = {};
    }

    async deleteAcademic() {
        try {
            await this.academicService.deleteAcademic(this.academic._jv.id)
            this.$emit('updated')
            this.toastSuccess.detail = 'Academic Qualifications Deleted'
            this.$toast.add(this.toastSuccess);
        } catch (e) {
            this.toastError.detail = e.message
            this.$toast.add(this.toastError);
            this.academicService.logError(e).forEach(err => {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Details',
                    detail: err
                });
            })
        }

        this.deleteAcademicDialog = false;
        this.academic = {};
    }

    async deleteSelectedAcademics() {
        try {
            for (const deleteAcademic of this.selectedAcademics) {
                await this.academicService.deleteAcademic(deleteAcademic._jv.id)
            }
            this.$emit('updated')
            this.toastSuccess.detail = 'Academic Qualifications Deleted'
            this.$toast.add(this.toastSuccess);
        } catch (e) {
            this.toastError.detail = e.message
            this.$toast.add(this.toastError);
            this.academicService.logError(e).forEach(err => {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Details',
                    detail: err
                });
            })
        }
        this.deleteAcademicsDialog = false;
        this.selectedAcademics = [];
    }

    async toggleSelectedAcademics() {
        try {
            if (this.verifyAcademics) {
                // verify
                for (const verifyAcademic of this.selectedAcademics) {
                    const academicObj = {
                        'verified': 1,
                        'verified-by': store.state.staffId,
                        'verified-at': this.$moment().toISOString(),
                        _jv: {
                            id: verifyAcademic._jv.id,
                            type: 'academics',
                            relationships: {
                                staff: {data: {id: verifyAcademic.staff._jv.id, type: 'staffs'}}
                            }
                        }
                    };
                    await this.academicService.updateAcademic(academicObj)
                }
            }

            if (!this.verifyAcademics) {
                // unverify
                for (const verifyAcademic of this.selectedAcademics) {
                    const academicObj = {
                        'verified': 0,
                        'verified-by': null,
                        'verified-at': null,
                        _jv: {
                            id: verifyAcademic._jv.id,
                            type: 'academics',
                            relationships: {
                                staff: {data: {id: verifyAcademic.staff._jv.id, type: 'staffs'}}
                            }
                        }
                    };
                    await this.academicService.updateAcademic(academicObj)
                }
            }

            this.$emit('updated')
            this.toastSuccess.detail = 'Academic Qualifications Updated'
            this.$toast.add(this.toastSuccess);
        } catch (e) {
            this.toastError.detail = e.message
            this.$toast.add(this.toastError);
            this.academicService.logError(e).forEach(err => {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Details',
                    detail: err,
                });
            })
        }

        this.toggleAcademicsDialog = false;
        this.selectedAcademics = [];
    }
}


</script>

<style scoped>

</style>
